// Renderer-neutral G03 data boundary. No product, DOM, asset loader or GPU imports.
export const RENDER_SCENE_LIMITS = Object.freeze({
  depth: 48, values: 2000000, stringCharacters: 4000000, arrayLength: 500000,
  geometries: 20000, styles: 10000, layers: 1024, nodes: 100000,
  pointsPerPath: 16384, totalPoints: 500000, holes: 1024, dashEntries: 32,
  idLength: 256, textLength: 16384, coordinate: 1e9, groupDepth: 32
});
const COLLECTIONS = Object.freeze(['geometries', 'styles', 'layers', 'nodes']);
const FORBIDDEN_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
const validatedRecords = new WeakMap();
// Only fully frozen source trees are eligible. Weak keys retain no discarded
// provider revisions; values are detached records validated at this boundary.
const normalizedSources = new WeakMap();

export class SceneContractError extends TypeError {
  constructor(code, message, path = '') {
    super(message);
    this.name = 'SceneContractError';
    this.code = code;
    this.path = path;
  }
}
function fail(code, message, path) { throw new SceneContractError(code, message, path); }
function check(condition, message, path, code = 'SCENE_CONTRACT_INVALID') {
  if (!condition) fail(code, message, path);
}
function budget() { return { values: 0, characters: 0, points: 0 }; }
function countValue(state, path) {
  state.values++;
  check(state.values <= RENDER_SCENE_LIMITS.values, 'Scene value budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
}
function countString(value, state, path) {
  state.characters += value.length;
  check(state.characters <= RENDER_SCENE_LIMITS.stringCharacters, 'Scene string budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
}
function descriptors(value, path, array = false) {
  check(value !== null && typeof value === 'object' && Array.isArray(value) === array,
    array ? 'Expected an array.' : 'Expected a plain data record.', path);
  const prototype = Object.getPrototypeOf(value);
  check(array ? prototype === Array.prototype : prototype === Object.prototype || prototype === null,
    'Custom prototypes and renderer objects are forbidden.', path, 'SCENE_DATA_FORBIDDEN');
  const keys = Reflect.ownKeys(value);
  check(keys.length <= RENDER_SCENE_LIMITS.values, 'Scene property budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
  const properties = new Map();
  for (const key of keys) {
    check(typeof key === 'string' && !FORBIDDEN_KEYS.has(key), 'Unsafe scene property.', path, 'SCENE_DATA_FORBIDDEN');
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    check(descriptor && Object.hasOwn(descriptor, 'value') && (descriptor.enumerable || array && key === 'length'),
      'Accessors and hidden properties are forbidden.', `${path}.${key}`, 'SCENE_DATA_FORBIDDEN');
    properties.set(key, descriptor.value);
  }
  if (array) {
    const length = properties.get('length');
    check(Number.isSafeInteger(length) && length >= 0 && length <= RENDER_SCENE_LIMITS.arrayLength,
      'Array length budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
    check(properties.size === length + 1, 'Sparse or extended arrays are forbidden.', path, 'SCENE_DATA_FORBIDDEN');
    for (let i = 0; i < length; i++) check(properties.has(String(i)), 'Sparse arrays are forbidden.', path, 'SCENE_DATA_FORBIDDEN');
  }
  return properties;
}
function cloneData(value, state, path, ancestors = new WeakSet(), depth = 0, immutable = null) {
  countValue(state, path);
  check(depth <= RENDER_SCENE_LIMITS.depth, 'Scene nesting budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string') { countString(value, state, path); return value; }
  if (typeof value === 'number') {
    check(Number.isFinite(value), 'Scene numbers must be finite.', path);
    return value;
  }
  check(typeof value === 'object', 'Only JSON data is accepted.', path, 'SCENE_DATA_FORBIDDEN');
  check(!ancestors.has(value), 'Cyclic data is forbidden.', path, 'SCENE_DATA_FORBIDDEN');
  if (immutable && !Object.isFrozen(value)) immutable.value = false;
  const array = Array.isArray(value);
  const properties = descriptors(value, path, array);
  ancestors.add(value);
  let result;
  if (array) {
    result = [];
    for (let i = 0; i < properties.get('length'); i++) {
      result.push(cloneData(properties.get(String(i)), state, `${path}[${i}]`, ancestors, depth + 1, immutable));
    }
  } else {
    result = {};
    for (const [key, entry] of properties) {
      countString(key, state, path);
      result[key] = cloneData(entry, state, `${path}.${key}`, ancestors, depth + 1, immutable);
    }
  }
  ancestors.delete(value);
  return result;
}
function fields(value, required, optional, path) {
  check(value !== null && typeof value === 'object' && !Array.isArray(value), 'Expected a data record.', path);
  const allowed = new Set([...required, ...optional]);
  for (const key of Object.keys(value)) check(allowed.has(key), `Unknown scene field ${key}.`, `${path}.${key}`, 'SCENE_FIELD_FORBIDDEN');
  for (const key of required) check(Object.hasOwn(value, key), `Missing scene field ${key}.`, `${path}.${key}`);
}
function id(value, path) {
  check(typeof value === 'string' && value.length > 0 && value.length <= RENDER_SCENE_LIMITS.idLength
    && value.trim() === value && ![...value].some(char => char.charCodeAt(0) <= 31 || char.charCodeAt(0) === 127)
    && !FORBIDDEN_KEYS.has(value), 'Invalid stable scene ID.', path);
}
function integer(value, path, minimum = 0) {
  check(Number.isSafeInteger(value) && value >= minimum, 'Expected a safe integer revision/order.', path);
}
function finite(value, path) {
  check(typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= RENDER_SCENE_LIMITS.coordinate,
    'Expected a bounded finite coordinate.', path);
}
function positive(value, path) { finite(value, path); check(value > 0, 'Expected a positive length.', path); }
function flag(value, path) { check(typeof value === 'boolean', 'Expected a boolean.', path); }
function enumeration(value, choices, path) { check(choices.includes(value), 'Unsupported scene value.', path); }
function point(value, path) {
  check(Array.isArray(value) && value.length === 2, 'Expected a plan x/z pair.', path);
  value.forEach((entry, i) => finite(entry, `${path}[${i}]`));
}
function transform(value, path) {
  check(Array.isArray(value) && value.length === 6, 'Expected a six-component plan transform.', path);
  value.forEach((entry, i) => finite(entry, `${path}[${i}]`));
  const determinant = value[0] * value[3] - value[1] * value[2];
  check(Number.isFinite(determinant) && determinant !== 0, 'Singular plan transform.', path);
}
function color(value, path) {
  check(Array.isArray(value) && value.length === 4 && value.every(entry => typeof entry === 'number' && entry >= 0 && entry <= 1),
    'Expected unpremultiplied RGBA components in [0,1].', path);
}
function points(value, path, state, closed = false) {
  check(Array.isArray(value) && value.length >= (closed ? 4 : 2) && value.length <= RENDER_SCENE_LIMITS.pointsPerPath,
    'Invalid or excessive path point count.', path, 'SCENE_BUDGET_EXCEEDED');
  state.points += value.length;
  check(state.points <= RENDER_SCENE_LIMITS.totalPoints, 'Scene point budget exceeded.', path, 'SCENE_BUDGET_EXCEEDED');
  value.forEach((entry, i) => point(entry, `${path}[${i}]`));
  if (closed) {
    check(value[0][0] === value.at(-1)[0] && value[0][1] === value.at(-1)[1], 'Closed rings must repeat their first point.', path);
    check(new Set(value.slice(0, -1).map(entry => `${entry[0]},${entry[1]}`)).size >= 3, 'A ring needs three distinct points.', path);
  }
}
function base(value, path) { id(value.id, `${path}.id`); integer(value.revision, `${path}.revision`, 1); }
function asset(value, path) {
  fields(value, ['identity', 'occurrenceId', 'viewId', 'projection'], [], path);
  fields(value.identity, ['assetId', 'revisionId', 'variantId'], [], `${path}.identity`);
  for (const key of ['assetId', 'revisionId', 'variantId']) id(value.identity[key], `${path}.identity.${key}`);
  id(value.occurrenceId, `${path}.occurrenceId`); id(value.viewId, `${path}.viewId`);
  const artifact = value.projection;
  fields(artifact, ['path', 'sha256', 'byteLength', 'mediaType'], [], `${path}.projection`);
  check(typeof artifact.path === 'string' && artifact.path.length > 0 && artifact.path.length <= 2048
    && !/[\\%?#:]/.test(artifact.path) && ![...artifact.path].some(char => char.charCodeAt(0) <= 32 || char.charCodeAt(0) === 127)
    && artifact.path.split('/').every(segment => segment !== '' && segment !== '.' && segment !== '..'),
  'Asset paths must be package-relative immutable artifacts.', `${path}.projection.path`);
  check(typeof artifact.sha256 === 'string' && /^[0-9a-f]{64}$/.test(artifact.sha256), 'Expected a lowercase SHA-256.', `${path}.projection.sha256`);
  integer(artifact.byteLength, `${path}.projection.byteLength`);
  enumeration(artifact.mediaType, ['application/json'], `${path}.projection.mediaType`);
}
function geometry(value, path, state) {
  const common = ['id', 'revision', 'kind'];
  const required = {
    line: ['start', 'end'], polyline: ['points', 'closed', 'approximation'],
    circle: ['center', 'radius'], 'circular-arc': ['center', 'radius', 'startAngle', 'sweepAngle'],
    ellipse: ['center', 'radiusX', 'radiusZ', 'rotation'],
    'elliptic-arc': ['center', 'radiusX', 'radiusZ', 'rotation', 'startAngle', 'sweepAngle'],
    rect: ['x', 'z', 'width', 'height'], polygon: ['outer', 'holes', 'approximation'],
    'asset-projection': ['asset', 'bounds']
  };
  check(Object.hasOwn(required, value?.kind), 'Unsupported geometry kind.', `${path}.kind`);
  fields(value, [...common, ...required[value.kind]], [], path); base(value, path);
  if (value.kind === 'line') { point(value.start, `${path}.start`); point(value.end, `${path}.end`); }
  if (value.kind === 'polyline') {
    flag(value.closed, `${path}.closed`); points(value.points, `${path}.points`, state, value.closed);
  }
  if (Object.hasOwn(value, 'center')) point(value.center, `${path}.center`);
  for (const key of ['radius', 'radiusX', 'radiusZ', 'width', 'height']) if (Object.hasOwn(value, key)) positive(value[key], `${path}.${key}`);
  for (const key of ['rotation', 'startAngle', 'x', 'z']) if (Object.hasOwn(value, key)) finite(value[key], `${path}.${key}`);
  if (Object.hasOwn(value, 'sweepAngle')) check(typeof value.sweepAngle === 'number' && Math.abs(value.sweepAngle) > 0
    && Math.abs(value.sweepAngle) <= 2 * Math.PI, 'Arc sweep must be nonzero and at most one full turn.', `${path}.sweepAngle`);
  if (Object.hasOwn(value, 'approximation')) enumeration(value.approximation, ['exact-linear', 'sampled-unverified'], `${path}.approximation`);
  if (value.kind === 'polygon') {
    points(value.outer, `${path}.outer`, state, true);
    check(Array.isArray(value.holes) && value.holes.length <= RENDER_SCENE_LIMITS.holes, 'Hole budget exceeded.', `${path}.holes`, 'SCENE_BUDGET_EXCEEDED');
    value.holes.forEach((ring, i) => points(ring, `${path}.holes[${i}]`, state, true));
  }
  if (value.kind === 'asset-projection') {
    asset(value.asset, `${path}.asset`);
    fields(value.bounds, ['minX', 'minZ', 'maxX', 'maxZ'], [], `${path}.bounds`);
    for (const key of ['minX', 'minZ', 'maxX', 'maxZ']) finite(value.bounds[key], `${path}.bounds.${key}`);
    check(value.bounds.minX <= value.bounds.maxX && value.bounds.minZ <= value.bounds.maxZ, 'Inverted display bounds.', `${path}.bounds`);
  }
}
function style(value, path) {
  fields(value, ['id', 'revision'], ['fill', 'stroke', 'assetFill', 'hiddenStroke'], path); base(value, path);
  check(Object.hasOwn(value, 'fill') || Object.hasOwn(value, 'stroke') || Object.hasOwn(value, 'assetFill'), 'A style needs fill or stroke.', path);
  if (Object.hasOwn(value, 'assetFill')) {
    enumeration(value.assetFill, ['region-colors'], `${path}.assetFill`);
    check(!Object.hasOwn(value, 'fill'), 'Choose intrinsic asset paint or an explicit fill override.', path);
  }
  if (value.fill) {
    fields(value.fill, ['color', 'rule'], [], `${path}.fill`); color(value.fill.color, `${path}.fill.color`);
    enumeration(value.fill.rule, ['evenodd', 'nonzero'], `${path}.fill.rule`);
  } else check(!Object.hasOwn(value, 'fill'), 'Fill cannot be null.', `${path}.fill`);
  for (const key of ['stroke', 'hiddenStroke']) {
    if (value[key]) {
      const stroke = value[key];
      fields(stroke, ['color', 'width', 'unit', 'cap', 'join', 'miterLimit'], ['dash', 'dashOffset'], `${path}.${key}`);
      color(stroke.color, `${path}.${key}.color`); positive(stroke.width, `${path}.${key}.width`);
      enumeration(stroke.unit, ['model-mm', 'css-px'], `${path}.${key}.unit`);
      enumeration(stroke.cap, ['butt', 'round', 'square'], `${path}.${key}.cap`);
      enumeration(stroke.join, ['miter', 'round', 'bevel'], `${path}.${key}.join`);
      finite(stroke.miterLimit, `${path}.${key}.miterLimit`); check(stroke.miterLimit >= 1, 'Miter limit must be at least one.', `${path}.${key}.miterLimit`);
      if (Object.hasOwn(stroke, 'dash')) {
        check(Array.isArray(stroke.dash) && stroke.dash.length > 0 && stroke.dash.length <= RENDER_SCENE_LIMITS.dashEntries,
          'Invalid dash pattern length.', `${path}.${key}.dash`);
        stroke.dash.forEach((entry, i) => positive(entry, `${path}.${key}.dash[${i}]`));
      }
      if (Object.hasOwn(stroke, 'dashOffset')) {
        check(Object.hasOwn(stroke, 'dash'), 'Dash offset requires a dash pattern.', `${path}.${key}.dashOffset`);
        finite(stroke.dashOffset, `${path}.${key}.dashOffset`);
      }
    } else check(!Object.hasOwn(value, key), 'Stroke cannot be null.', `${path}.${key}`);
  }
}
function layer(value, path) {
  fields(value, ['id', 'revision', 'order', 'visible'], [], path); base(value, path);
  integer(value.order, `${path}.order`, -Number.MAX_SAFE_INTEGER); flag(value.visible, `${path}.visible`);
}
function node(value, path) {
  const common = ['id', 'revision', 'kind', 'objectId', 'layerId', 'order', 'visible', 'transform'];
  const required = { geometry: ['geometryId', 'styleId'], text: ['text', 'anchor', 'font', 'color', 'align', 'baseline', 'direction'],
    group: ['children', 'opacity'] };
  check(Object.hasOwn(required, value?.kind), 'Unsupported scene node kind.', `${path}.kind`);
  fields(value, [...common, ...required[value.kind]], value.kind === 'group' ? ['featureId', 'clip'] : value.kind === 'text' ? ['featureId', 'halo'] : ['featureId'], path);
  base(value, path); id(value.objectId, `${path}.objectId`); id(value.layerId, `${path}.layerId`);
  if (Object.hasOwn(value, 'featureId')) id(value.featureId, `${path}.featureId`);
  integer(value.order, `${path}.order`, -Number.MAX_SAFE_INTEGER); flag(value.visible, `${path}.visible`); transform(value.transform, `${path}.transform`);
  if (value.kind === 'geometry') { id(value.geometryId, `${path}.geometryId`); id(value.styleId, `${path}.styleId`); }
  if (value.kind === 'text') {
    check(typeof value.text === 'string' && value.text.length <= RENDER_SCENE_LIMITS.textLength
      && !value.text.includes('\r') && !value.text.includes('\n') && !value.text.includes('\0'),
      'Text must be a bounded single line.', `${path}.text`);
    point(value.anchor, `${path}.anchor`); color(value.color, `${path}.color`);
    enumeration(value.align, ['left', 'center', 'right'], `${path}.align`);
    enumeration(value.baseline, ['alphabetic', 'middle'], `${path}.baseline`); enumeration(value.direction, ['ltr'], `${path}.direction`);
    fields(value.font, ['family', 'size', 'unit', 'weight', 'style', 'kerning'], [], `${path}.font`);
    check(typeof value.font.family === 'string' && value.font.family.length > 0 && value.font.family.length <= 256
      && /^[-\p{L}\p{N}_][\p{L}\p{N}_ -]*(?:, *[\p{L}\p{N}_][\p{L}\p{N}_ -]*)*$/u.test(value.font.family),
    'Expected plain local font family names, not CSS declarations or URLs.', `${path}.font.family`);
    positive(value.font.size, `${path}.font.size`); enumeration(value.font.unit, ['model-mm', 'css-px'], `${path}.font.unit`);
    enumeration(value.font.weight, ['normal', 'bold', 100, 200, 300, 400, 500, 600, 700, 800, 900], `${path}.font.weight`); enumeration(value.font.style, ['normal', 'italic'], `${path}.font.style`);
    enumeration(value.font.kerning, ['normal'], `${path}.font.kerning`);
    if (Object.hasOwn(value, 'halo')) {
      style({ id: value.id, revision: value.revision, stroke: value.halo }, `${path}.halo`);
    }
  }
  if (value.kind === 'group') {
    check(Array.isArray(value.children) && value.children.length <= RENDER_SCENE_LIMITS.nodes, 'Invalid group children.', `${path}.children`);
    value.children.forEach((entry, i) => id(entry, `${path}.children[${i}]`));
    check(new Set(value.children).size === value.children.length, 'Duplicate child node.', `${path}.children`);
    check(typeof value.opacity === 'number' && value.opacity >= 0 && value.opacity <= 1, 'Invalid group opacity.', `${path}.opacity`);
    if (Object.hasOwn(value, 'clip')) {
      fields(value.clip, ['geometryId', 'transform'], [], `${path}.clip`);
      id(value.clip.geometryId, `${path}.clip.geometryId`); transform(value.clip.transform, `${path}.clip.transform`);
    }
  }
}
const validators = { geometries: geometry, styles: style, layers: layer, nodes: node };
function recordCost(value, cost = { values: 0, characters: 0 }) {
  cost.values++;
  if (typeof value === 'string') cost.characters += value.length;
  if (value !== null && typeof value === 'object') {
    for (const [key, entry] of Object.entries(value)) {
      if (!Array.isArray(value)) cost.characters += key.length;
      recordCost(entry, cost);
    }
  }
  return cost;
}
function validateRecords(graph) {
  const total = { values: 5, characters: 0, points: 0 };
  for (const collection of COLLECTIONS) {
    const records = graph[collection];
    check(Array.isArray(records) && records.length <= RENDER_SCENE_LIMITS[collection], 'Scene record budget exceeded.', collection, 'SCENE_BUDGET_EXCEEDED');
    const ids = new Set();
    records.forEach((record, index) => {
      let metadata = validatedRecords.get(record);
      if (metadata) {
        check(metadata.collection === collection, 'Scene record used in the wrong collection.', `${collection}[${index}]`);
      } else {
        const state = budget();
        validators[collection](record, `${collection}[${index}]`, state);
        metadata = { collection, points: state.points, ...recordCost(record) };
        freezeDeep(record);
        validatedRecords.set(record, metadata);
      }
      total.values += metadata.values;
      total.characters += metadata.characters;
      total.points += metadata.points;
      check(total.values <= RENDER_SCENE_LIMITS.values && total.characters <= RENDER_SCENE_LIMITS.stringCharacters
        && total.points <= RENDER_SCENE_LIMITS.totalPoints, 'Combined scene data budget exceeded.', collection, 'SCENE_BUDGET_EXCEEDED');
      check(!ids.has(record.id), 'Duplicate scene record ID.', `${collection}[${index}].id`, 'SCENE_ID_DUPLICATE'); ids.add(record.id);
    });
  }
}
function validateLinks(graph) {
  const maps = Object.fromEntries(COLLECTIONS.map(collection => [collection, new Map(graph[collection].map(record => [record.id, record]))]));
  const parents = new Map();
  for (const entry of graph.nodes) {
    const path = `nodes.${entry.id}`;
    check(maps.layers.has(entry.layerId), 'Missing node layer.', path, 'SCENE_REFERENCE_MISSING');
    if (entry.kind === 'geometry') {
      check(maps.geometries.has(entry.geometryId), 'Missing node geometry.', path, 'SCENE_REFERENCE_MISSING');
      check(maps.styles.has(entry.styleId), 'Missing node style.', path, 'SCENE_REFERENCE_MISSING');
      const paint = maps.styles.get(entry.styleId);
      check((!Object.hasOwn(paint, 'assetFill') && !Object.hasOwn(paint, 'hiddenStroke'))
        || maps.geometries.get(entry.geometryId).kind === 'asset-projection',
      'Intrinsic region paint and hidden strokes require asset geometry.', path);
    }
    if (entry.kind !== 'group') continue;
    if (entry.clip) check(maps.geometries.get(entry.clip.geometryId)?.kind === 'rect', 'Clip requires owner-local rect geometry.', path, 'SCENE_CLIP_INVALID');
    for (const childId of entry.children) {
      const child = maps.nodes.get(childId);
      check(child, 'Missing group child.', path, 'SCENE_REFERENCE_MISSING');
      check(!parents.has(childId), 'Scene nodes cannot have multiple parents.', path, 'SCENE_PARENT_CONFLICT');
      check(child.layerId === entry.layerId, 'Group children must remain in the same layer.', path, 'SCENE_LAYER_CONFLICT');
      parents.set(childId, entry.id);
    }
  }
  const complete = new Map(); const active = new Set();
  function visit(entry) {
    if (complete.has(entry.id)) return complete.get(entry.id);
    check(!active.has(entry.id), 'Cyclic scene groups.', `nodes.${entry.id}`, 'SCENE_GROUP_CYCLE');
    check(active.size < RENDER_SCENE_LIMITS.groupDepth, 'Group nesting budget exceeded.', `nodes.${entry.id}`, 'SCENE_BUDGET_EXCEEDED');
    active.add(entry.id);
    let depth = 1;
    if (entry.kind === 'group') for (const childId of entry.children) depth = Math.max(depth, 1 + visit(maps.nodes.get(childId)));
    check(depth <= RENDER_SCENE_LIMITS.groupDepth, 'Group nesting budget exceeded.', `nodes.${entry.id}`, 'SCENE_BUDGET_EXCEEDED');
    active.delete(entry.id); complete.set(entry.id, depth);
    return depth;
  }
  for (const entry of graph.nodes) visit(entry);
}
function freezeDeep(value) {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value)) freezeDeep(child);
    Object.freeze(value);
  }
  return value;
}

// Inspect envelope and arrays on every call. Cached records still contribute
// their entire data cost; collection ownership and graph links are rechecked.
function cloneUpdate(value, state, candidates) {
  countValue(state, 'scene');
  const properties = descriptors(value, 'scene'), update = {};
  const ancestors = new WeakSet([value]);
  for (const [key, entry] of properties) {
    const path = `scene.${key}`;
    countString(key, state, path);
    if (!COLLECTIONS.includes(key) || !Array.isArray(entry)) {
      update[key] = cloneData(entry, state, path, ancestors, 1);
      continue;
    }
    countValue(state, path);
    const records = descriptors(entry, path, true), result = [];
    ancestors.add(entry);
    for (let i = 0; i < records.get('length'); i++) {
      const source = records.get(String(i)), recordPath = `${path}[${i}]`;
      const known = validatedRecords.has(source) ? source : normalizedSources.get(source);
      if (known) {
        const cost = validatedRecords.get(known);
        state.values += cost.values; state.characters += cost.characters;
        check(state.values <= RENDER_SCENE_LIMITS.values && state.characters <= RENDER_SCENE_LIMITS.stringCharacters,
          'Scene data budget exceeded.', recordPath, 'SCENE_BUDGET_EXCEEDED');
        result.push(known);
      } else {
        const immutable = { value: true };
        const record = cloneData(source, state, recordPath, ancestors, 2, immutable);
        result.push(record);
        if (immutable.value && source !== null && typeof source === 'object') candidates.push([source, record]);
      }
    }
    ancestors.delete(entry); update[key] = result;
  }
  return update;
}

/** Detached, immutable records; deltas validate references after the host applies them atomically. */
export function normalizeRenderSceneUpdate(value) {
  const state = budget(), candidates = []; const update = cloneUpdate(value, state, candidates);
  const common = ['contract', 'contractVersion', 'mode', 'revision', ...COLLECTIONS];
  fields(update, update?.mode === 'delta' ? [...common, 'baseRevision'] : common,
    update?.mode === 'delta' ? ['remove'] : [], 'scene');
  check(update.contract === 'ks.render-scene' && update.contractVersion === 1, 'Unsupported scene contract/version.', 'scene');
  enumeration(update.mode, ['replace', 'delta'], 'scene.mode'); integer(update.revision, 'scene.revision', 1);
  validateRecords(update);
  if (update.mode === 'delta') {
    integer(update.baseRevision, 'scene.baseRevision');
    check(update.revision > update.baseRevision, 'A delta must advance its base revision.', 'scene.revision');
    if (!Object.hasOwn(update, 'remove')) update.remove = Object.fromEntries(COLLECTIONS.map(collection => [collection, []]));
    fields(update.remove, COLLECTIONS, [], 'scene.remove');
    for (const collection of COLLECTIONS) {
      const removed = update.remove[collection];
      check(Array.isArray(removed) && removed.length <= RENDER_SCENE_LIMITS[collection], 'Removal budget exceeded.', `scene.remove.${collection}`);
      removed.forEach((entry, i) => id(entry, `scene.remove.${collection}[${i}]`));
      check(new Set(removed).size === removed.length, 'Duplicate removal ID.', `scene.remove.${collection}`);
      const upserted = new Set(update[collection].map(entry => entry.id));
      check(removed.every(entry => !upserted.has(entry)), 'Cannot remove and upsert the same ID.', `scene.remove.${collection}`);
    }
  } else validateLinks(update);
  freezeDeep(update);
  for (const [source, record] of candidates) normalizedSources.set(source, record);
  return update;
}

/** Validate a complete prospective graph. Known frozen records are reused without geometry copies. */
export function validateRenderSceneGraph(value) {
  const properties = descriptors(value, 'graph');
  check(properties.size === COLLECTIONS.length && COLLECTIONS.every(collection => properties.has(collection)), 'Expected the four scene collections.', 'graph');
  const graph = {}; const state = budget();
  for (const collection of COLLECTIONS) {
    const records = descriptors(properties.get(collection), collection, true);
    check(records.get('length') <= RENDER_SCENE_LIMITS[collection], 'Scene record budget exceeded.', collection, 'SCENE_BUDGET_EXCEEDED');
    graph[collection] = [];
    for (let i = 0; i < records.get('length'); i++) {
      const record = records.get(String(i));
      graph[collection].push(validatedRecords.has(record) ? record : cloneData(record, state, `${collection}[${i}]`));
    }
  }
  validateRecords(graph); validateLinks(graph);
  return true;
}
